/**
 * Route admin `/tutor/admin/moves` — le catalogue des Moves de la trousse (migration 0752).
 *
 * La liste (gestes / connaissances, actifs ou non), puis la fiche d'un Move : libellé, phrase du cours,
 * type générique, ligne posée (connaissance) ou famille de touches ouverte (geste), HORA du déblocage,
 * niveaux, actif, débloqué par défaut, place des touches, script qui l'enseigne, ordre, écritures du
 * déblocage (une juste, les autres sont des erreurs qui disent pourquoi), SKILLCARDS associées, note.
 * Un geste ne peut ouvrir qu'une famille de touches qui existe dans le code ; une connaissance se crée
 * entièrement d'ici. L'app relit le catalogue à chaque feuille ouverte.
 */
import { useMemo, useState } from 'react';
import { ArrowDown, ArrowLeft, ArrowUp, Plus, Shapes, Trash2, X } from 'lucide-react';
import { SECONDARY_SCHOOL_LEVELS, type SchoolLevel } from '@jeraisonne/db';
import { Button, cn } from '@jeraisonne/ui';
import {
  MOVE_PLACEMENTS,
  MOVE_SCRIPTS,
  MOVE_TYPES,
  type MoveChoice,
  type MoveDef,
  type MoveGesture,
  type MoveKind,
  type MovePlacement,
} from '@/features/moves/move-catalog';
import { moveLevels } from '@/features/moves/move-catalog-store';
import {
  SkillcardPickerField,
  toSkillcardPickerItem,
} from '@/features/skillcards/skillcard-picker';
import {
  blankMove,
  gestureKeysLabel,
  moveFormIssues,
  useAdminMoves,
  useSaveMove,
} from '@/lib/admin/moves';
import { useCards } from '@/lib/cards/api';

const LEVELS = SECONDARY_SCHOOL_LEVELS;
const LEVEL_LABEL: Record<string, string> = Object.fromEntries(
  LEVELS.map((l) => [l.value, l.label]),
);

/** Les familles de touches qu'un geste peut ouvrir : celles qui existent dans le clavier. */
const GESTURE_OPTIONS: { id: MoveGesture; label: string }[] = [
  { id: 'commute', label: 'Commuter (flèches ← → et glissé)' },
  { id: 'substitute', label: 'Remplacer une lettre par sa valeur' },
  { id: 'cross', label: 'Passer de l’autre côté du signe égal' },
  { id: 'distribute', label: 'Distribuer terme à terme' },
  { id: 'develop', label: 'Développer (identités du cours)' },
  { id: 'factor', label: 'Factoriser' },
  { id: 'crossmul', label: 'Produit en croix' },
];

type Filter = 'all' | 'geste' | 'savoir' | 'inactive';

const field =
  'border-ink-200 text-ink-900 w-full rounded-xl border bg-white px-3 py-2 text-sm min-h-11 ocean:bg-[#0b1733]';

export function TutorAdminMovesPage() {
  const q = useAdminMoves();
  const moves = useMemo(() => q.data ?? [], [q.data]);
  const [filter, setFilter] = useState<Filter>('all');
  const [editing, setEditing] = useState<{ move: MoveDef; isNew: boolean } | null>(null);

  const shown = useMemo(
    () =>
      moves.filter((m) =>
        filter === 'all' ? true : filter === 'inactive' ? !m.active : m.kind === filter,
      ),
    [moves, filter],
  );

  if (editing)
    return (
      <MoveEditor
        initial={editing.move}
        isNew={editing.isNew}
        all={moves}
        onClose={() => setEditing(null)}
      />
    );

  const nextPosition = (moves.at(-1)?.position ?? 0) + 10;
  return (
    <div className="mx-auto max-w-4xl px-4 py-6 sm:py-8">
      <h1 className="font-display text-ink-900 flex items-center gap-2 text-2xl font-extrabold">
        <Shapes className="h-6 w-6" aria-hidden /> Moves
      </h1>
      <p className="text-ink-500 mt-1 text-sm">
        Ce que les élèves sortent de leur trousse : des gestes (violet) et des connaissances (or).
        L’app relit ce catalogue à chaque feuille ouverte.
      </p>

      <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-wrap gap-2" role="group" aria-label="Filtrer">
          {(
            [
              ['all', `Tous (${moves.length})`],
              ['geste', 'Gestes'],
              ['savoir', 'Connaissances'],
              ['inactive', 'Inactifs'],
            ] as [Filter, string][]
          ).map(([id, label]) => (
            <button
              key={id}
              type="button"
              aria-pressed={filter === id}
              onClick={() => setFilter(id)}
              className={cn(
                'min-h-11 rounded-full px-4 text-sm font-semibold',
                filter === id ? 'bg-primary-500 text-white' : 'bg-ink-100 text-ink-700',
              )}
            >
              {label}
            </button>
          ))}
        </div>
        <Button
          variant="primary"
          className="w-full sm:w-auto"
          onClick={() => setEditing({ move: blankMove(nextPosition), isNew: true })}
        >
          <Plus className="h-4 w-4" aria-hidden /> Nouveau Move
        </Button>
      </div>

      {q.isLoading && <p className="text-ink-500 mt-6 text-sm">Chargement…</p>}
      {q.error && (
        <p role="alert" className="text-error-600 mt-6 text-sm">
          {(q.error as Error).message}
        </p>
      )}

      <ul className="mt-4 space-y-2" data-testid="admin-moves">
        {shown.map((m) => (
          <li key={m.id}>
            <button
              type="button"
              onClick={() => setEditing({ move: m, isNew: false })}
              className="border-ink-200 hover:bg-ink-50 flex w-full items-center gap-3 rounded-2xl border p-3 text-left active:scale-[0.98]"
            >
              <span
                aria-hidden
                className={cn(
                  'flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-lg font-bold',
                  m.kind === 'geste'
                    ? 'bg-[#efe9fc] text-[#5b32b0]'
                    : 'bg-[#fdf0c8] text-[#8a6508]',
                )}
              >
                {m.kind === 'geste' ? '↹' : '✦'}
              </span>
              <span className="min-w-0 flex-1">
                <span className="text-ink-900 block truncate text-sm font-bold">{m.name}</span>
                <span className="text-ink-500 block text-xs">
                  {MOVE_TYPES.find((t) => t.id === m.type)?.label ?? m.type} · {m.hora} HORA ·{' '}
                  {levelRange(m.levels)}
                  {m.cardIds.length > 0 &&
                    ` · ${m.cardIds.length} SKILLCARD${m.cardIds.length > 1 ? 'S' : ''}`}
                </span>
              </span>
              <span className="flex shrink-0 flex-col items-end gap-1">
                {!m.active && <Pill tone="muted">Inactif</Pill>}
                {m.unlockedByDefault && <Pill tone="ok">Par défaut</Pill>}
                {m.script && <Pill tone="muted">Script</Pill>}
              </span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

function Pill({ tone, children }: { tone: 'ok' | 'muted'; children: string }) {
  return (
    <span
      className={cn(
        'rounded-full px-2 py-0.5 text-xs font-semibold',
        tone === 'ok' ? 'bg-[#e8f5ee] text-[#1d6b45]' : 'bg-ink-100 text-ink-600',
      )}
    >
      {children}
    </span>
  );
}

function levelRange(levels: readonly string[]): string {
  if (!levels.length) return '—';
  const a = LEVEL_LABEL[levels[0]!] ?? levels[0];
  const b = LEVEL_LABEL[levels.at(-1)!] ?? levels.at(-1);
  return a === b ? `${a}` : `${a} → ${b}`;
}

// ── la fiche d'un Move ──────────────────────────────────────────────────────

function MoveEditor({
  initial,
  isNew,
  all,
  onClose,
}: {
  initial: MoveDef;
  isNew: boolean;
  all: readonly MoveDef[];
  onClose: () => void;
}) {
  const [m, setM] = useState<MoveDef>(() => ({
    ...initial,
    choices: initial.choices.map((c) => ({ ...c })),
    cardIds: [...initial.cardIds],
  }));
  const save = useSaveMove();
  const cards = useCards();
  const [saved, setSaved] = useState(false);
  const set = (patch: Partial<MoveDef>) => {
    setSaved(false);
    setM((x) => ({ ...x, ...patch }));
  };
  // une connaissance pose sa ligne juste : c'est la même écriture
  const effective: MoveDef =
    m.kind === 'savoir' ? { ...m, writes: m.choices.find((c) => c.ok)?.text ?? '' } : m;
  const issues = moveFormIssues(effective, all, isNew);
  const cardItems = useMemo(
    () => (cards.data ?? []).filter((c) => c.status === 'published').map(toSkillcardPickerItem),
    [cards.data],
  );
  const cardTitle = (id: string) => cardItems.find((c) => c.id === id)?.title ?? id.slice(0, 8);
  const minLevel = (m.levels[0] ?? '6e') as SchoolLevel;
  const maxLevel = (m.levels.at(-1) ?? 'tle') as SchoolLevel;

  const setKind = (kind: MoveKind) =>
    set({
      kind,
      type: MOVE_TYPES.find((t) => t.kind === kind)!.id,
      writes: kind === 'savoir' ? (m.writes ?? '') : undefined,
      gesture: kind === 'geste' ? (m.gesture ?? 'substitute') : undefined,
      placement: kind === 'geste' ? m.placement : null,
    });

  const setChoice = (i: number, patch: Partial<MoveChoice>) =>
    set({ choices: m.choices.map((c, k) => (k === i ? { ...c, ...patch } : c)) });
  const setRight = (i: number) =>
    set({
      choices: m.choices.map((c, k) => {
        const { ok: _ok, ...rest } = c;
        return k === i ? { ...rest, ok: true as const } : rest;
      }),
    });
  const moveChoice = (i: number, d: -1 | 1) => {
    const j = i + d;
    if (j < 0 || j >= m.choices.length) return;
    const next = [...m.choices];
    [next[i], next[j]] = [next[j]!, next[i]!];
    set({ choices: next });
  };

  const submit = async () => {
    if (issues.length) return;
    await save.mutateAsync({ move: effective, isNew });
    setSaved(true);
    if (isNew) onClose();
  };

  return (
    <div className="mx-auto max-w-3xl px-4 py-6 pb-28 sm:py-8">
      <button
        type="button"
        onClick={onClose}
        className="text-ink-600 hover:text-ink-900 -ml-2 flex min-h-11 items-center gap-1 px-2 text-sm font-semibold"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden /> Tous les Moves
      </button>
      <h1 className="font-display text-ink-900 mt-2 text-2xl font-extrabold">
        {isNew ? 'Nouveau Move' : m.name || 'Move'}
      </h1>

      <div className="mt-5 space-y-5">
        <Section title="Identité">
          {isNew && (
            <Label text="Identifiant (définitif)" hint="minuscules et tirets, ex. « aire-disque »">
              <input
                className={field}
                value={m.id}
                onChange={(e) => set({ id: e.target.value.trim().toLowerCase() })}
              />
            </Label>
          )}
          <Label text="Libellé">
            <input
              className={field}
              value={m.name}
              maxLength={80}
              onChange={(e) => set({ name: e.target.value })}
            />
          </Label>
          <Label text="Phrase du cours" hint="montrée sur la carte une fois le Move débloqué">
            <textarea
              className={cn(field, 'min-h-20')}
              value={m.why}
              maxLength={600}
              onChange={(e) => set({ why: e.target.value })}
            />
          </Label>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Label text="Famille">
              <select
                className={field}
                value={m.kind}
                disabled={!isNew}
                onChange={(e) => setKind(e.target.value as MoveKind)}
              >
                <option value="savoir">Connaissance (or)</option>
                <option value="geste">Geste (violet)</option>
              </select>
            </Label>
            <Label text="Type générique">
              <select
                className={field}
                value={m.type}
                onChange={(e) => set({ type: e.target.value as MoveDef['type'] })}
              >
                {MOVE_TYPES.filter((t) => t.kind === m.kind).map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.label}
                  </option>
                ))}
              </select>
            </Label>
          </div>
          {m.kind === 'geste' && (
            <Label
              text="Famille de touches ouverte"
              hint={`touches du clavier : ${gestureKeysLabel(m)} — un nouveau geste demande du code`}
            >
              <select
                className={field}
                value={m.gesture ?? ''}
                onChange={(e) => set({ gesture: e.target.value as MoveGesture })}
              >
                {GESTURE_OPTIONS.map((g) => (
                  <option key={g.id} value={g.id}>
                    {g.label}
                  </option>
                ))}
              </select>
            </Label>
          )}
        </Section>

        <Section title="Récompense et niveaux">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <Label text="HORA au déblocage" hint="de 50 à 1 000, une fois par compte">
              <input
                type="number"
                min={50}
                max={1000}
                step={10}
                className={field}
                value={m.hora}
                onChange={(e) => set({ hora: Number(e.target.value) })}
              />
            </Label>
            <Label text="Niveau minimum">
              <select
                className={field}
                value={minLevel}
                onChange={(e) => set({ levels: moveLevels(e.target.value, maxLevel) })}
              >
                {LEVELS.map((l) => (
                  <option key={l.value} value={l.value}>
                    {l.label}
                  </option>
                ))}
              </select>
            </Label>
            <Label text="Niveau maximum" hint="sert à choisir ses voisines">
              <select
                className={field}
                value={maxLevel}
                onChange={(e) => set({ levels: moveLevels(minLevel, e.target.value) })}
              >
                {LEVELS.map((l) => (
                  <option key={l.value} value={l.value}>
                    {l.label}
                  </option>
                ))}
              </select>
            </Label>
          </div>
        </Section>

        <Section title="Comportement">
          <Toggle
            label="Actif"
            hint="inactif : jamais proposé aux élèves, et ses touches restent libres"
            value={m.active}
            onChange={(active) => set({ active })}
          />
          <Toggle
            label="Débloqué par défaut"
            hint="acquis d’office : ses touches sont là dès le départ, sans HORA"
            value={m.unlockedByDefault}
            onChange={(unlockedByDefault) => set({ unlockedByDefault })}
          />
          {m.kind === 'geste' && (
            <Label
              text="Place des touches"
              hint={m.gesture === 'commute' ? 'les flèches ← → restent dans le dock' : undefined}
            >
              <select
                className={field}
                value={m.placement ?? ''}
                disabled={m.gesture === 'commute'}
                onChange={(e) =>
                  set({ placement: (e.target.value || null) as MovePlacement | null })
                }
              >
                <option value="">Par défaut</option>
                {MOVE_PLACEMENTS.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.label}
                  </option>
                ))}
              </select>
            </Label>
          )}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Label text="Script qui l’enseigne">
              <select
                className={field}
                value={m.script ?? ''}
                onChange={(e) => set({ script: e.target.value || null })}
              >
                <option value="">Aucun</option>
                {MOVE_SCRIPTS.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.label}
                  </option>
                ))}
              </select>
            </Label>
            <Label text="Ordre dans la trousse" hint="à pertinence égale, le plus petit d’abord">
              <input
                type="number"
                className={field}
                value={m.position}
                onChange={(e) => set({ position: Number(e.target.value) })}
              />
            </Label>
          </div>
        </Section>

        <Section
          title="Écritures du déblocage"
          hint={
            m.kind === 'savoir'
              ? 'L’élève reconnaît la bonne parmi les erreurs classiques. La juste est la ligne posée sur sa feuille : elle doit être une formule que la feuille accepte.'
              : 'Un geste se prend tel quel dans la trousse ; ces écritures montrent la règle et ses erreurs classiques.'
          }
        >
          <ul className="space-y-3">
            {m.choices.map((c, i) => (
              <li key={i} className="border-ink-200 space-y-2 rounded-xl border p-3">
                <div className="flex items-center gap-2">
                  <label className="flex min-h-11 items-center gap-2 text-sm font-semibold">
                    <input
                      type="radio"
                      name="right-choice"
                      checked={!!c.ok}
                      onChange={() => setRight(i)}
                      className="h-5 w-5"
                    />
                    La juste
                  </label>
                  <span className="flex-1" />
                  <IconButton label="Monter" onClick={() => moveChoice(i, -1)} disabled={i === 0}>
                    <ArrowUp className="h-4 w-4" />
                  </IconButton>
                  <IconButton
                    label="Descendre"
                    onClick={() => moveChoice(i, 1)}
                    disabled={i === m.choices.length - 1}
                  >
                    <ArrowDown className="h-4 w-4" />
                  </IconButton>
                  <IconButton
                    label="Retirer cette écriture"
                    onClick={() => set({ choices: m.choices.filter((_, k) => k !== i) })}
                  >
                    <Trash2 className="h-4 w-4" />
                  </IconButton>
                </div>
                <input
                  className={field}
                  aria-label={`Écriture ${i + 1}`}
                  value={c.text}
                  maxLength={200}
                  placeholder="ex. A = b × h ÷ 2"
                  onChange={(e) => setChoice(i, { text: e.target.value })}
                />
                <input
                  className={field}
                  aria-label={`Ce qu’on dit pour l’écriture ${i + 1}`}
                  value={c.why}
                  maxLength={400}
                  placeholder={c.ok ? 'ex. Oui : …' : 'ex. Ça, c’est le périmètre : …'}
                  onChange={(e) => setChoice(i, { why: e.target.value })}
                />
              </li>
            ))}
          </ul>
          {m.choices.length < 8 && (
            <Button
              variant="secondary"
              className="mt-2 w-full sm:w-auto"
              onClick={() => set({ choices: [...m.choices, { text: '', why: '' }] })}
            >
              <Plus className="h-4 w-4" aria-hidden /> Ajouter une erreur classique
            </Button>
          )}
        </Section>

        <Section
          title="SKILLCARDS associées"
          hint="quand la question est reliée à l’une d’elles, ce Move passe devant dans la trousse"
        >
          {m.cardIds.length > 0 && (
            <ul className="flex flex-wrap gap-2">
              {m.cardIds.map((id) => (
                <li
                  key={id}
                  className="bg-ink-100 text-ink-800 flex items-center gap-1 rounded-full py-1 pl-3 pr-1 text-sm"
                >
                  {cardTitle(id)}
                  <button
                    type="button"
                    aria-label={`Retirer ${cardTitle(id)}`}
                    onClick={() => set({ cardIds: m.cardIds.filter((x) => x !== id) })}
                    className="flex h-9 w-9 items-center justify-center rounded-full"
                  >
                    <X className="h-4 w-4" aria-hidden />
                  </button>
                </li>
              ))}
            </ul>
          )}
          <SkillcardPickerField
            items={cardItems.filter((c) => !m.cardIds.includes(c.id))}
            value={null}
            placeholder="Associer une SKILLCARD…"
            onChange={(id) => id && set({ cardIds: [...m.cardIds, id] })}
          />
        </Section>

        <Section title="Note interne">
          <textarea
            className={cn(field, 'min-h-20')}
            aria-label="Note interne"
            value={m.notes}
            maxLength={2000}
            onChange={(e) => set({ notes: e.target.value })}
          />
        </Section>
      </div>

      <div className="ocean:bg-[#061433]/95 border-ink-200 fixed inset-x-0 bottom-0 z-30 border-t bg-white/95 px-4 pb-[max(0.75rem,env(safe-area-inset-bottom))] pt-3 backdrop-blur">
        <div className="mx-auto max-w-3xl space-y-2">
          {issues.length > 0 && (
            <ul className="text-error-600 text-xs" data-testid="move-issues">
              {issues.map((x) => (
                <li key={x}>· {x}</li>
              ))}
            </ul>
          )}
          {save.error && (
            <p role="alert" className="text-error-600 text-sm">
              {(save.error as Error).message}
            </p>
          )}
          <Button
            variant="primary"
            fullWidth
            disabled={issues.length > 0 || save.isPending}
            onClick={() => void submit()}
          >
            {save.isPending
              ? 'Enregistrement…'
              : saved
                ? 'Enregistré ✓'
                : isNew
                  ? 'Créer le Move'
                  : 'Enregistrer'}
          </Button>
        </div>
      </div>
    </div>
  );
}

function Section({
  title,
  hint,
  children,
}: {
  title: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="border-ink-200 space-y-3 rounded-2xl border p-4">
      <div>
        <h2 className="font-display text-ink-900 text-base font-bold">{title}</h2>
        {hint && <p className="text-ink-500 mt-0.5 text-xs">{hint}</p>}
      </div>
      {children}
    </section>
  );
}

function Label({
  text,
  hint,
  children,
}: {
  text: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block space-y-1">
      <span className="text-ink-800 block text-sm font-semibold">{text}</span>
      {children}
      {hint && <span className="text-ink-500 block text-xs">{hint}</span>}
    </label>
  );
}

function Toggle({
  label,
  hint,
  value,
  onChange,
}: {
  label: string;
  hint?: string;
  value: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <div className="flex items-start gap-3">
      <span className="min-w-0 flex-1">
        <span className="text-ink-900 block text-sm font-semibold">{label}</span>
        {hint && <span className="text-ink-500 block text-xs">{hint}</span>}
      </span>
      <button
        type="button"
        role="switch"
        aria-checked={value}
        aria-label={label}
        onClick={() => onChange(!value)}
        className="flex min-h-11 min-w-11 shrink-0 items-center justify-center"
      >
        <span
          className={cn(
            'relative h-6 w-11 rounded-full transition-colors',
            value ? 'bg-primary-500' : 'bg-ink-200',
          )}
        >
          <span
            className={cn(
              'absolute top-0.5 h-5 w-5 rounded-full bg-[#ffffff] shadow transition-transform',
              value ? 'translate-x-[22px]' : 'translate-x-0.5',
            )}
          />
        </span>
      </button>
    </div>
  );
}

function IconButton({
  label,
  onClick,
  disabled,
  children,
}: {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      disabled={disabled}
      onClick={onClick}
      className="text-ink-500 hover:text-ink-900 flex min-h-11 min-w-11 items-center justify-center rounded-xl disabled:opacity-30"
    >
      {children}
    </button>
  );
}
